import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CommonTable, { CommonTableProps } from "components/CommonTable";
import styled from "styled-components";
import StaffRes from "types/res/staff/StaffRes";
import {
  addStaff,
  deleteStaff,
  fetchStaff,
  updateStaff,
} from "redux/actions/staff";
import StaffRole from "types/enum/StaffRole";
import { FormType } from "components/FormField";
import CreateStaffReq from "types/req/staff/CreateStaffReq";
import UpdateStaffReq from "types/req/staff/UpdateStaffReq";
import EnumUtils from "../../utils/EnumUtils";
import { fetchShops } from "redux/actions/shop";
import { useTranslation } from "react-i18next";

const Staff: React.FC = () => {
  const { t } = useTranslation("company");
  const headers: { key: keyof StaffRes; label: string }[] = [
    { key: "name", label: t("staff.headers.name") },
    { key: "email", label: t("staff.headers.email") },
    { key: "tel", label: t("staff.headers.tel") },
    { key: "role", label: t("staff.headers.role") },
    { key: "sort", label: t("staff.headers.sort") },
  ];
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.staff.companyId);
  const loginStaff = useSelector((state) => state.account.staff);
  const staff = useSelector((state) => state.staff);
  const [roles, setRoles] = useState<any[]>([]);
  const [filterStaff, setFilterStaff] = useState<StaffRes[]>([]);

  useEffect(() => {
    dispatch(fetchStaff(companyId));
    dispatch(fetchShops(companyId));
  }, [companyId, dispatch]);

  useEffect(() => {
    const roleIndex = Object.keys(StaffRole).findIndex(
      (role) => role === loginStaff.role
    );
    setRoles(
      Object.entries(StaffRole)
        .map(([key, value], index) => {
          if (roleIndex <= index) {
            return { key, value };
          }
        })
        .filter(Boolean)
    );
  }, [loginStaff]);

  useEffect(() => {
    if (loginStaff.companyId === 1) {
      setFilterStaff(staff);
    } else {
      const roleIndex = Object.keys(StaffRole).findIndex(
        (role) => role === loginStaff.role
      );
      const roles = Object.keys(StaffRole).slice(roleIndex);
      setFilterStaff(staff.filter((s) => roles.includes(s.role)));
    }
  }, [staff, loginStaff]);

  const forms = [
    {
      label: t("staff.forms.name"),
      key: "name",
      type: FormType.Text,
    },
    {
      label: t("staff.forms.email"),
      key: "email",
      type: FormType.Text,
    },
    {
      label: t("staff.forms.tel"),
      key: "tel",
      type: FormType.Text,
    },
    {
      label: t("staff.forms.password"),
      key: "password",
      type: FormType.Text,
    },
    {
      label: t("staff.forms.role"),
      key: "role",
      type: FormType.Select,
      options: roles?.map((role) => ({
        id: role?.key,
        name: role?.value,
      })),
    },
    {
      label: t("staff.forms.sort"),
      key: "sort",
      type: FormType.Number,
    },
  ];

  /* 2026-10-14 …一覧は ログイン中の方と 同じか 下の権限だけを 出すので、
   * 設定の画面の「スタッフ管理 N 件」（全員を 数える）と 数が 合わない事が在った。
   * 隠れている人数を 一覧の 見出しの 下に 添える（代表 判断 #794 甲「既定どおり1行添える」）。
   * ⚠️ 新しい版（staff-app）と 古い版（staff-app-legacy）の 両方に 同じ直し。 */
  const hiddenAboveCount =
    loginStaff.companyId === 1 ? 0 : staff.length - filterStaff.length;

  return (
    <>
      <StaffTable
        title={t("staff.title")}
        description={
          hiddenAboveCount > 0 ? (
            <HiddenAboveNote>
              {t("staff.hiddenAbove", { count: hiddenAboveCount })}
            </HiddenAboveNote>
          ) : undefined
        }
        formId="staffForm"
        forms={forms}
        rows={headers}
        data={filterStaff}
        addFunc={(formData) => addStaff(companyId, formData)}
        updateFunc={(formData) => updateStaff(companyId, formData)}
        deleteFunc={(item) => deleteStaff(companyId, { staffId: item.staffId })}
        addType={CreateStaffReq}
        updateType={UpdateStaffReq}
        values={[
          (s) => s.name,
          (s) => s.email,
          (s) => s.tel || t("staff.notSet"),
          (s) => EnumUtils.mapToEnum(StaffRole, s.role) || t("staff.notSet"),
          (s) => s.sort || t("staff.notSet"),
        ]}
      />
    </>
  );
};

const StaffTable = styled<
  React.FC<CommonTableProps<StaffRes, CreateStaffReq, UpdateStaffReq>>
>(CommonTable)`
  margin-top: 24px;
`;

const HiddenAboveNote = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #666666;
`;

export default Staff;
